import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { SimulationResponse } from '../services/api';

// Define the simulation step type
export interface SimulationStep {
  id: string;
  label: string;
  completed: boolean;
}

// Define the initial state
interface SimulationState {
  isLoading: boolean;
  error: string | null;
  formData: {
    content: string;
    platform: string;
    sender: string;
    sector: string;
    accountId: string;
    simCount: number;
    imageFile: File | null;
    imagePreviewUrl?: string;
    imageDescription?: string;
    isPrivate: boolean;
  };
  results: SimulationResponse | null;
  simulationHistory: SimulationResponse[];
  simulationSteps: SimulationStep[];
  currentStep: string | null;
  completedSteps?: string[];
}

// Default simulation steps
const defaultSimulationSteps: SimulationStep[] = [
  { id: 'image_description', label: 'Generating Image Description', completed: false },
  { id: 'media_description', label: 'Processing Media Description', completed: false },
  { id: 'platform_params', label: 'Getting Platform Parameters', completed: false },
  { id: 'user_data', label: 'Preparing User Data', completed: false },
  { id: 'forecast', label: 'Generating Forecast', completed: false },
  { id: 'reactions', label: 'Generating Sample Reactions', completed: false },
  { id: 'completion', label: 'Finalizing Results', completed: false }
];

const initialState: SimulationState = {
  isLoading: false,
  error: null,
  formData: {
    content: '',
    platform: 'Twitter/X',
    sender: 'a brand',
    sector: 'Consumer Goods',
    accountId: '',
    simCount: 10,
    imageFile: null,
    imagePreviewUrl: '',
    imageDescription: '',
    isPrivate: false,
  },
  results: null,
  simulationHistory: [],
  simulationSteps: [...defaultSimulationSteps],
  currentStep: null,
  completedSteps: [],
};

// Define action types
type ActionType =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_FORM'; payload: Partial<SimulationState['formData']> }
  | { type: 'SET_RESULTS'; payload: SimulationResponse }
  | { type: 'RESET_FORM' }
  | { type: 'ADD_TO_HISTORY'; payload: SimulationResponse }
  | { type: 'SET_CURRENT_STEP'; payload: string }
  | { type: 'COMPLETE_STEP'; payload: string }
  | { type: 'RESET_SIMULATION_STEPS' };

// Create the reducer
const simulationReducer = (state: SimulationState, action: ActionType): SimulationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { 
        ...state, 
        isLoading: action.payload,
        // When loading is set to true, reset steps. When loading is set to false, complete all steps.
        ...(action.payload 
          ? { 
              simulationSteps: state.formData.imageFile
                ? [
                    // When an image is uploaded, include image_description, exclude media_description
                    ...defaultSimulationSteps.filter(step => step.id !== 'media_description')
                  ]
                : [
                    // When no image file but media description may be present, exclude image_description
                    ...defaultSimulationSteps.filter(step => step.id !== 'image_description')
                  ],
              currentStep: null,
              completedSteps: [] // Reset completed steps when starting a new simulation
            }
          : { 
              simulationSteps: state.simulationSteps.map(step => ({ ...step, completed: true })),
              currentStep: null
            }
        )
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'UPDATE_FORM':
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };
    case 'SET_RESULTS':
      return { ...state, results: action.payload, isLoading: false };
    case 'RESET_FORM':
      return {
        ...state,
        formData: initialState.formData,
        error: null,
        results: null,
      };
    case 'ADD_TO_HISTORY':
      // Only add to history if it's not a duplicate
      if (!state.simulationHistory.some(item => JSON.stringify(item) === JSON.stringify(action.payload))) {
        return {
          ...state,
          simulationHistory: [action.payload, ...state.simulationHistory].slice(0, 10), // Keep only last 10
        };
      }
      return state;
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
        completedSteps: state.completedSteps || []
      };
    case 'COMPLETE_STEP':
      return {
        ...state,
        simulationSteps: state.simulationSteps.map(step => 
          step.id === action.payload 
            ? { ...step, completed: true }
            : step
        ),
        completedSteps: [...(state.completedSteps || []), action.payload]
      };
    case 'RESET_SIMULATION_STEPS':
      return {
        ...state,
        simulationSteps: [...defaultSimulationSteps],
        currentStep: null,
        completedSteps: []
      };
    default:
      return state;
  }
};

// Create the context
interface SimulationContextType {
  state: SimulationState;
  dispatch: React.Dispatch<ActionType>;
}

const SimulationContext = createContext<SimulationContextType | undefined>(undefined);

// Create the provider component
interface SimulationProviderProps {
  children: ReactNode;
}

export const SimulationProvider: React.FC<SimulationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(simulationReducer, initialState);

  return (
    <SimulationContext.Provider value={{ state, dispatch }}>
      {children}
    </SimulationContext.Provider>
  );
};

// Create a custom hook for using the context
export const useSimulation = () => {
  const context = useContext(SimulationContext);
  if (context === undefined) {
    throw new Error('useSimulation must be used within a SimulationProvider');
  }
  return context;
}; 